import type { CSSProperties, FC, ReactNode } from 'react'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Counter } from '@/components/generals/counter'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitWords } from '@/components/generals/split-words'

type Lang = 'no' | 'en' | 'pl'

const LANGS: Array<{ id: Lang, label: string, name: string }> = [
    { id: 'no', label: 'NO', name: 'Norsk' },
    { id: 'en', label: 'EN', name: 'English' },
    { id: 'pl', label: 'PL', name: 'Polski' }
]

const SHEET: Record<Lang, { title: string, items: string[], ok: string, fault: string, na: string, lang: string }> = {
    no: {
        lang: 'nb',
        title: 'Utførelse av tømrerarbeid i våtrom',
        items: [
            'Er fall mot sluk minst 1:50 i dusj, og 1:100 for øvrig?',
            'Er sluket plassert minst 300 mm fra vegg?'
        ],
        ok: 'OK',
        fault: 'Feil',
        na: 'IR'
    },
    en: {
        lang: 'en',
        title: 'Carpentry work in wet rooms',
        items: [
            'Is the fall towards the drain at least 1:50 in the shower, and 1:100 elsewhere?',
            'Is the drain placed at least 300 mm from the wall?'
        ],
        ok: 'OK',
        fault: 'Fault',
        na: 'N/A'
    },
    pl: {
        lang: 'pl',
        title: 'Prace stolarskie w pomieszczeniach mokrych',
        items: [
            'Czy spadek do odpływu wynosi co najmniej 1:50 pod prysznicem i 1:100 w pozostałej części?',
            'Czy odpływ znajduje się co najmniej 300 mm od ściany?'
        ],
        ok: 'OK',
        fault: 'Błąd',
        na: 'N/D'
    }
}

const SHELF = ['Rehab', 'Våtrom', 'Nybygg', 'TEK17', 'Egne maler', 'Tildelt prosjekt', 'Norsk', 'English', 'Polski']

/** The template library: a thousand checklists, one of them shown in the crew's own language. */
export const TemplateLibrary: FC = (): ReactNode => {
    const [lang, setLang] = useState<Lang>('no')
    const sheet = SHEET[lang]

    return (
        <section id='maler' className='tone-butter relative overflow-hidden pt-24 md:pt-36' data-header='light'>
            <div className='wrap grid grid-cols-12 gap-x-6 gap-y-14'>
                <div className='col-span-12 lg:col-span-6'>
                    <SectionLabel index='03'>Sjekklister</SectionLabel>

                    <p className='ks-thousand mt-6 tabular' aria-hidden='true'>
                        <Counter value='1000' />
                    </p>
                    <h2 className='t-h2 mt-4 max-w-[18ch] balance' data-reveal='words'>
                        <span className='sr-only'>1000 </span>
                        <SplitWords text='bransjemaler. Eller lag dine egne.' />
                    </h2>
                    <p className='mt-6 max-w-md text-tone-muted pretty'>
                        Over 1000 ferdige sjekklister for rehab, våtrom og nybygg etter TEK17. Bruk dem som de er, tilpass dem, eller lag dine egne, og tildel dem til prosjektet de hører til.
                    </p>
                </div>

                <div className='col-span-12 lg:col-span-5 lg:col-start-8'>
                    <div className='flex flex-wrap items-center justify-between gap-4'>
                        <p className='t-label text-tone-muted'>Samme sjekkliste, tre språk</p>

                        <div className='flex border border-night' role='group' aria-label='Velg språk på sjekklisten'>
                            {LANGS.map(option => (
                                <button
                                    key={option.id}
                                    type='button'
                                    onClick={() => setLang(option.id)}
                                    aria-pressed={lang === option.id}
                                    title={option.name}
                                    className={cn(
                                        't-label min-h-11 min-w-12 px-3 transition-colors duration-300',
                                        lang === option.id ? 'bg-night text-paper' : 'text-night hover:bg-night/10'
                                    )}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className='mt-5 bg-chalk text-night shadow-[0_40px_80px_-40px_rgb(36_29_45/0.45)]' lang={sheet.lang}>
                        <div key={`${lang}-head`} className='animate-in fade-in slide-in-from-bottom-2 border-b border-steel px-5 py-4 duration-500 md:px-6'>
                            <p className='t-label text-slate'>06-S010 · 06</p>
                            <p className='mt-1.5 text-lg font-semibold leading-tight tracking-tight'>{sheet.title}</p>
                        </div>

                        <ol className='px-5 md:px-6'>
                            {sheet.items.map((item, i) => (
                                <li
                                    key={`${lang}-${item}`}
                                    className='animate-in fade-in slide-in-from-bottom-3 border-b border-steel/60 py-4 duration-500 last:border-b-0'
                                    style={{ animationDelay: `${(i + 1) * 70}ms`, animationFillMode: 'both' } as CSSProperties}
                                >
                                    <p className='text-sm font-medium leading-snug'>{item}</p>
                                    <p className='mt-2.5 flex gap-2' aria-hidden='true'>
                                        <span className='check-chip is-on'>{sheet.ok}</span>
                                        <span className='check-chip'>{sheet.fault}</span>
                                        <span className='check-chip'>{sheet.na}</span>
                                    </p>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </div>

            <div className='mt-20 overflow-hidden border-y border-tone-line py-5 md:mt-28' aria-hidden='true'>
                <div className='marquee' style={{ '--marquee-duration': '46s' } as CSSProperties}>
                    {[0, 1].map(copy => (
                        <div key={copy} className='flex shrink-0 items-center'>
                            {SHELF.map(word => (
                                <span key={`${copy}-${word}`} className='ks-shelf-word flex items-center'>
                                    {word}
                                    <span className='not-sep' />
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )
}
